import React from 'react';
import type { GlobalProps, ImageProps } from '@bloomberg/media-ui-core';
import { classes, Image, RecentTimestamp } from '@bloomberg/media-ui-core';
import { Play } from '@bloomberg/media-ui-icons';

import { sanitizeSpaces } from '../utils';

import styles from './StoryListItem.module.css';

export type StoryListItemProps = GlobalProps & {
  displayImage?: boolean;
  displayTimestamp?: boolean;
  eyebrow?: string;
  headline?: string;
  onClick?: () => void;
  thumbnail?: Partial<ImageProps>;
  href?: string;
  type?: string;
  duration?: string;
  publishedAt?: string;
  updatedAt?: string;
  showTimestampDateAfterCutoff?: boolean;
  timestampCutoffHours?: number;
  variant?: 'default' | 'dark';
};

const ASPECT_RATIO = 4 / 3;
const IMAGE_WIDTH = 94;

export const StoryListItem = React.forwardRef<
  HTMLDivElement,
  StoryListItemProps
>(function StoryListItemComponent(props: StoryListItemProps, ref) {
  const {
    displayImage,
    displayTimestamp = true,
    eyebrow,
    onClick,
    thumbnail,
    href,
    headline,
    type,
    duration,
    publishedAt,
    updatedAt,
    timestampCutoffHours,
    showTimestampDateAfterCutoff,
    variant,
  } = props;

  const cx = classes([styles.storyListItem], props);
  const isVideo = 'video' === type;
  const cleanHeadline = headline ? sanitizeSpaces(headline) : '';

  return (
    <div className={cx} ref={ref}>
      {thumbnail?.src && displayImage && (
        <a className={styles.listItemLink} href={href} onClick={onClick}>
          <div className={styles.listItemImage}>
            <Image
              altText={thumbnail.altText}
              aspectRatio={ASPECT_RATIO}
              fetchpriority="low"
              sizes={`${IMAGE_WIDTH}px`}
              src={thumbnail.src}
              width={IMAGE_WIDTH}
              useResponsivePNG
            />
            {isVideo && duration && (
              <div className={styles.duration}>
                <Play />
                {duration}
              </div>
            )}
          </div>
        </a>
      )}
      <div className={styles.listItemStory}>
        {eyebrow && (
          <div
            className={classes([
              styles.eyebrow,
              { [styles.dark]: eyebrow && variant === 'dark' },
            ])}
          >
            {eyebrow}
          </div>
        )}
        <a
          className={classes([
            styles.listItemLink,
            { [styles.dark]: cleanHeadline && variant === 'dark' },
          ])}
          href={href}
          onClick={onClick}
        >
          {cleanHeadline}
        </a>
        {(publishedAt || updatedAt) && displayTimestamp && (
          <RecentTimestamp
            publishedAt={publishedAt}
            updatedAt={updatedAt}
            showDateAfterCutoff={Boolean(showTimestampDateAfterCutoff)}
            cutoffHours={timestampCutoffHours}
            className={classes([
              styles.recentTimeStamp,
              { [styles.dark]: variant === 'dark' },
            ])}
          />
        )}
      </div>
    </div>
  );
});
